import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { supabase } from "@/integrations/supabase/client";
import { hasActiveDateFilter, type EncounterDateFilter } from "./encounterFilters";
import type { RawFilesViewerEncounter } from "./useEncounterById";

export interface EncounterFilters extends EncounterDateFilter {
  customerIds: string[];
  patientId: string | null;
}

export function hasActiveEncounterFilter(filters: EncounterFilters): boolean {
  return filters.customerIds.length > 0 || filters.patientId !== null || hasActiveDateFilter(filters);
}

export function useFilteredEncounters(filters: EncounterFilters) {
  return useQuery({
    queryKey: ["raw-files-viewer-encounters", filters],
    queryFn: async (): Promise<RawFilesViewerEncounter[]> => {
      let query = supabase
        .from("patient_encounters")
        .select("id, patient_id, admit_datetime, discharge_datetime, specialty")
        .order("discharge_datetime", { ascending: false })
        .limit(200);

      if (filters.customerIds.length > 0) {
        query = query.in("customer_id", filters.customerIds);
      }

      if (filters.patientId) {
        query = query.eq("patient_id", filters.patientId);
      }

      if (hasActiveDateFilter(filters)) {
        query = query.not("discharge_datetime", "is", null);
        if (filters.dischargeFrom) {
          query = query.gte(
            "discharge_datetime",
            dayjs.tz(dayjs(filters.dischargeFrom).format("YYYY-MM-DD"), "Europe/London").startOf("day").toISOString(),
          );
        }
        if (filters.dischargeTo) {
          query = query.lte(
            "discharge_datetime",
            dayjs.tz(dayjs(filters.dischargeTo).format("YYYY-MM-DD"), "Europe/London").endOf("day").toISOString(),
          );
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: hasActiveEncounterFilter(filters),
  });
}
